import AccordionItem from '@atoms/AccordionItem/AccordionItem';
import Button from '@atoms/Button/Button';
import Checkbox from '@atoms/Checkbox/Checkbox';
import Icon from '@atoms/Icon/Icon';
import IconButton from '@atoms/IconButton/IconButton';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import FullScreenModal from '@atoms/Modal/FullScreenModal';
import Rows from '@atoms/Rows/Rows';
import Search from '@atoms/Search/Search';
import Text from '@atoms/Text/Text';
import { IFilter } from '@models/IFilterGroup';
import IFormValues from '@models/IFormValues';
import IProductDatasheets, {
  IDatasheetTypeSetChildren,
  IProductDatasheet,
} from '@models/IProductDatasheets';
import {
  IProductDatasheetsResponse,
  ITechnicalDownloadSearch,
  ITechnicalDownloadSearchFormData,
} from '@models/ITechnicalDownloadSearch';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import SizeEnum from '@models/enums/SizeEnum';
import Accordion from '@molecules/Accordion/Accordion';
import ExpandableBanner from '@molecules/ExpandableBanner/ExpandableBanner';
import ListingPlaceholder from '@molecules/ListingPlaceholder/ListingPlaceholder';
import { breakpointsAbove } from '@utility/config/breakpoints';
import ensureArray from '@utility/helpers/ensureArray';
import extractDocumentsFromProductsArray from '@utility/helpers/extractDocumentsFromProductsArray';
import removeObjectKeys from '@utility/helpers/removeObjectKeys';
import replaceValuesInLabel from '@utility/helpers/replaceValuesInLabel';
import useBreakpoint from '@utility/hooks/useBreakpoint';
import useListingComponentData from '@utility/hooks/useListingComponentData';
import classNames from 'classnames';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import DownloadItemRow from './components/DownloadItemRow';
import FilterSection from './components/FilterSection';

const TechnicalDownloadSearchPage = ({
  addAllLabel,
  addLabel,
  apiEndpoint,
  applyLabel,
  closeLabel,
  collapseLabel,
  countries,
  countryPlaceholder,
  currentCulture,
  defaultCountryCode,
  documentsAddedLabel,
  documentsLabel,
  documentsShownLabel,
  downloadAsMultipleLabel,
  downloadAsSingleLabel,
  downloadLabel,
  expandLabel,
  filterLabel,
  filters,
  header,
  industryNameForPageLoadTracking,
  languagePlaceholder,
  loadingLabel,
  noResultsHeader,
  noResultsLabel,
  noResultsText,
  noSearchTermHeader,
  noSearchTermText,
  openLabel,
  regionForPageLoadTracking,
  removeAllLabel,
  removeLabel,
  requiredErrorMessage,
  searchLabel,
  site,
  subIndustryNameForPageLoadTracking,
  subText,
  targetAudienceForPageLoadTracking,
}: ITechnicalDownloadSearch) => {
  const [expandAll, setExpandAll] = useState(false);
  const [submittedText, setSubmittedText] = useState('');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<IFilter | null>(
    null
  );
  const [selectedCountry, setSelectedCountry] = useState<IFilter | null>({
    label: countryPlaceholder,
    id: '',
  });
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<
    IProductDatasheet[]
  >([]);
  const [languageOptions, setLanguageOptions] = useState<IFilter[]>([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);

  const breakpoint = useBreakpoint();

  const searchEndpoint = `${apiEndpoint}/search`;

  const downloadEndpoint = `${apiEndpoint}/download/`;

  const dataTestIdPrefix = 'C26-TDS';

  // Get params from the current address in the client, but whitelisted by filters to ensure any other values are not converted
  const parsedUrlParams =
    typeof window !== 'undefined' &&
    queryString.parse(
      queryString.pick(window?.location?.search, [
        'freetext',
        'location',
        'language',
        filters.id as string,
      ]),
      { arrayFormat: 'comma' }
    );

  const initialSearchText = parsedUrlParams ? parsedUrlParams?.freetext : '';

  // Initialise listing component data
  const { fetchData, state } = useListingComponentData<
    ITechnicalDownloadSearchFormData,
    IProductDatasheetsResponse
  >(searchEndpoint, { products: [] });

  // Initialise react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      documents: [] as string[],
      ...parsedUrlParams,
    } as IFormValues,
  });

  // is add all checkbox checked
  const addAllChecked = watch('addAll');

  // country
  const country = watch('location') as string;

  // create an array of only documents from products data - they come from the api in a complex nested structure
  const allDocuments = extractDocumentsFromProductsArray(state.data.products);

  // create an array of all unique documents - the same documents can be listed in the 'children' for multiple top-level documents
  const allUniqueDocuments: IProductDatasheet[] = allDocuments.filter(
    (value, index, self) =>
      self.findIndex((obj) => obj.id === value.id) === index
  );

  // select all documents
  const selectAllDocuments = () => setSelectedDocuments(allDocuments);

  // remove a document
  const removeDocument = (documentId: number) =>
    setSelectedDocuments(
      selectedDocuments?.filter((document) => document.id !== documentId)
    );

  // remove all documents
  const removeAllDocuments = () => setSelectedDocuments([]);

  // Submit handler
  const onSubmit: SubmitHandler<IFormValues> = async (data, e) => {
    e?.preventDefault();

    await fetchData(
      removeObjectKeys(data, [
        'addAll',
        ...filters.options.map((filter) => filter.id as string),
      ])
    );
    setSubmittedText(`${data.freetext ?? ''}`);
    setValue('addAll', null);
  };

  // A shared function which will get languages and populate the language dropdown accordingly
  const setLanguages = async (countryItem: IFilter, reset: boolean = true) => {
    setSelectedLanguage(null);
    setLanguagesLoading(true);
    const response = await fetch(
      `${apiEndpoint}/country/languages/?countryCode=${countryItem.id}&siteCulture=${currentCulture}`
    );
    const data = (await response.json()) as IFilter[];

    setLanguageOptions(data?.length ? data : []);
    setLanguagesLoading(false);
    // This if statement is used for when this function is called in useEffect() to get languages first time around
    if (
      parsedUrlParams &&
      parsedUrlParams.language &&
      parsedUrlParams.location &&
      !reset
    ) {
      const language = data.find(
        (language) => language.id === parsedUrlParams.language
      );
      if (language) {
        setSelectedLanguage(language);
        setValue('language', language.id as string);
      }
    }
  };

  // user's default country takes precedence, but if that is not being passed then it will use whatever is in the query param
  useEffect(() => {
    const initialCountryCode =
      (parsedUrlParams && parsedUrlParams.location) || defaultCountryCode;

    const initialCountry = countries.find(
      (country) => country.id === initialCountryCode
    );

    if (initialCountry) {
      setSelectedCountry(initialCountry);
      setValue('location', initialCountry.id as string);
      setLanguages(initialCountry, false);
    }

    // Initial state for file categories
    const initialFileTypes =
      (parsedUrlParams as ITechnicalDownloadSearchFormData)[
        filters.id as string
      ] ?? filters.options.map((option) => option.id as string);

    setValue(filters.id as string, ensureArray(initialFileTypes));
    setSelectedFileTypes(ensureArray(initialFileTypes));

    // Only do an initial fetch if there is parameters in the query string
    if (
      parsedUrlParams &&
      (parsedUrlParams.freetext || (parsedUrlParams.freetext && initialCountry))
    ) {
      fetchData({
        ...parsedUrlParams,
        location: initialCountry?.id as string,
      });
    }
  }, []);

  useEffect(() => {
    document.documentElement.style.overflowY = 'auto';
  }, []);

  useEffect(() => {
    if (state.data.products.length) {
      window.dataLayer.push({
        event: 'trackingEvent_search_results',
        event_name_variable: 'search_results',
        trackingEvent_search_results: {
          search_type: 'datasheet',
          search_count: state.data.products.length,
          search_term: getValues('freetext'),
          document_type: selectedFileTypes
            .map((value) => value.toLocaleUpperCase())
            .sort(),
          document_language: selectedLanguage?.label ?? languagePlaceholder,
          document_country: selectedCountry?.label ?? countryPlaceholder,
        },
      });
    }
  }, [state.data.products]);

  const filterSectionProps = {
    countries: [{ label: countryPlaceholder, id: '' }, ...countries],
    countryPlaceholder,
    filters,
    onCountryChange: setLanguages,
    languagePlaceholder,
    languages: languageOptions,
    register,
    selectedLanguage,
    setSelectedLanguage,
    selectedCountry,
    setSelectedCountry,
    selectedFileTypes,
    setSelectedFileTypes,
    setValue,
    disableLanguageSelection: !country || languagesLoading,
  };

  return (
    <section className="tw-relative">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="tw-container tw-grid tw-grid-cols-4 tw-gap-x-xs tw-gap-y-2xl tw-pt-4xl tw-text-black md:tw-grid-cols-6 md:tw-gap-x-md lg:tw-grid-cols-12 lg:tw-pt-5xl"
      >
        <div className="tw-col-span-full tw-flex tw-flex-col tw-items-center tw-text-center md:tw-col-span-4 md:tw-col-start-2 lg:tw-col-span-8 xl:tw-col-span-6 xl:tw-col-start-4">
          <Text variant="hero-headline">{header}</Text>
          <Text variant="body" className="tw-mt-lg tw-text-grey-80">
            {subText}
          </Text>
        </div>
        <div className="tw-col-span-full tw-flex tw-flex-col tw-gap-xs">
          <Search
            autoCompleteEndpoint={`${searchEndpoint}/autocomplete?freetext`}
            dataTestId={`${dataTestIdPrefix}-Search-Input`}
            setValue={setValue}
            errors={errors}
            label={'Search'}
            name={'freetext'}
            requiredErrorMessage={requiredErrorMessage}
            initialValue={
              Array.isArray(initialSearchText) ? '' : initialSearchText ?? ''
            }
            register={register}
            placeholder={searchLabel}
          />
          {/*
          ---------------------------------------
          FILTERS MODAL / ACCORDION - MOBILE ONLY
          ---------------------------------------
          */}
          <button
            aria-label={openLabel}
            aria-controls="filters-modal"
            aria-expanded={isMobileFiltersOpen}
            onClick={() => setIsMobileFiltersOpen(true)}
            type="button"
            className="tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-[4px] tw-bg-grey-10 tw-p-xs tw-outline-none xl:tw-hidden"
            data-testid={`${dataTestIdPrefix}-Mobile-Filter-Button`}
          >
            {filterLabel}
            <Icon name="filter" />
          </button>
          <FullScreenModal
            closeLabel={closeLabel}
            title={filterLabel}
            isOpen={isMobileFiltersOpen}
            setIsOpen={setIsMobileFiltersOpen}
          >
            <div className="tw-flex-tw-flex-col tw-p-xs tw-pt-sm">
              <FilterSection
                {...filterSectionProps}
                dataTestId={dataTestIdPrefix}
              />
            </div>
            <div className="tw-mt-auto tw-p-xs">
              <Button
                site={site}
                buttonClasses="tw-w-full"
                onClick={(e) => {
                  e.preventDefault();
                  const submitHandler = handleSubmit(onSubmit);
                  submitHandler(e);
                  setIsMobileFiltersOpen(false);
                }}
                label={applyLabel}
                dataTestId={`${dataTestIdPrefix}-Mobile-Apply-Filter-Button`}
              />
            </div>
          </FullScreenModal>
          <Text variant="body-small" className="tw-block tw-pt-1 xl:tw-hidden">
            {allDocuments.length === 0
              ? noResultsLabel
              : replaceValuesInLabel(documentsShownLabel, [
                  allDocuments.length,
                ])}
          </Text>
        </div>
        {/*
        -------------------------------------
        DESKTOP FILTERS / PRODUCT COUNT LABEL
        -------------------------------------
        */}
        {breakpoint &&
          breakpointsAbove(BreakpointEnum.Large).includes(breakpoint) && (
            <div className="tw-hidden tw-flex-col xl:tw-col-span-3 xl:tw-flex">
              <Text variant="body-small" className="tw-pb-xs">
                {allDocuments.length === 0
                  ? noResultsLabel
                  : replaceValuesInLabel(documentsShownLabel, [
                      allUniqueDocuments.length,
                    ])}
              </Text>
              <div className="tw-mb-lg">
                <FilterSection
                  {...filterSectionProps}
                  dataTestId={dataTestIdPrefix}
                />
              </div>
              <Button
                site={site}
                onClick={(e) => {
                  e.preventDefault();
                  const submitHandler = handleSubmit(onSubmit);
                  submitHandler(e);
                }}
                variant="yellow"
                label={applyLabel}
                dataTestId={`${dataTestIdPrefix}-Desktop-Apply-Filter-Button`}
              />
            </div>
          )}
        {/*
        -----------------------------------------------------
        MAIN TABLE AREA - INCLUDING SECTIONS FOR ERROR STATES
        -----------------------------------------------------
        */}
        <div className="tw-col-span-full xl:tw-col-span-9">
          {state.loading && (
            <div className="tw-flex tw-w-full tw-justify-center tw-pt-9">
              <LoadingSpinner
                size={SizeEnum.Large}
                loadingLabel={loadingLabel}
              />
            </div>
          )}
          {/* USER HAS NOT ENTERED A SEARCH TERM */}
          {!state.loading &&
            !state.data.products.length &&
            !isSubmitSuccessful && (
              <ListingPlaceholder
                dataTestId={`${dataTestIdPrefix}-No-Search-Results`}
                iconName="find"
                title={noSearchTermHeader}
                message={noSearchTermText}
              />
            )}
          {/* NO PRODUCT FOUND FOR APPLIED FILTERS / TEXT */}
          {!state.loading &&
            isSubmitSuccessful &&
            state.data.products.length === 0 && (
              <ListingPlaceholder
                dataTestId={`${dataTestIdPrefix}-No-Filtered-Results`}
                title={replaceValuesInLabel(noResultsHeader, [submittedText])}
                message={noResultsText}
              />
            )}
          {/*
          --------------------------------
          MAIN DOCUMENTS TABLE / ACCORDION
          --------------------------------
          */}
          {!state.loading && !!state.data.products.length && (
            <>
              <div className="tw-flex tw-w-full tw-justify-between tw-pb-xs">
                <IconButton
                  icon={expandAll ? 'decrease' : 'expand'}
                  onClick={() => setExpandAll(!expandAll)}
                  label={expandAll ? collapseLabel : expandLabel}
                  dataTestId={`${dataTestIdPrefix}-${
                    expandAll ? 'Collapse-All-Button' : 'Expand-All-Button'
                  }`}
                />
                <Checkbox
                  name="addAll"
                  onClick={() =>
                    addAllChecked ? removeAllDocuments() : selectAllDocuments()
                  }
                  label={addAllChecked ? removeAllLabel : addAllLabel}
                  register={register}
                  dataTestId={`${dataTestIdPrefix}-Add-All-Button`}
                />
              </div>
              <Accordion variant="tableItem">
                {state.data.products.map(
                  (product: IProductDatasheets, index: number) => (
                    <AccordionItem
                      dataTestId={`${dataTestIdPrefix}-Accordion-Header-${index}`}
                      key={`${product.name} ${product.productId}`}
                      buttonClasses={classNames({
                        'tw-border-t tw-border-grey-20': index === 0,
                      })}
                      isOpen={expandAll}
                      variant="tableItem"
                      title={product.name}
                    >
                      {product.value.map((datasheetsSet, setIndex: number) => (
                        <div
                          key={datasheetsSet.name}
                          className={classNames(
                            setIndex > 0
                              ? 'tw-ml-xs lg:tw-ml-sm'
                              : 'tw-pl-xs lg:tw-pl-sm'
                          )}
                        >
                          <Rows
                            borderBottom={false}
                            borderTop={
                              !product.value[setIndex - 1]?.children.length
                            }
                          >
                            <Text
                              variant="body-14-semibold"
                              className="tw-py-xs"
                            >
                              {datasheetsSet.name}
                            </Text>
                            {datasheetsSet.value.map((datasheet) => (
                              <DownloadItemRow
                                dataTestId={dataTestIdPrefix}
                                selectedDocuments={selectedDocuments}
                                setSelectedDocuments={setSelectedDocuments}
                                removeDocument={removeDocument}
                                key={datasheet.id}
                                value={datasheet}
                                label={addLabel}
                                name={`${datasheet.name} (${datasheet.location} - ${datasheet.language})`}
                                addLabel={addLabel}
                                fileUrl={`${downloadEndpoint}merged?selectedFiles=${datasheet.id}`}
                                cultureCode={datasheet.cultureCode}
                                fileName={datasheet.fileName}
                                downloadLabel={downloadLabel}
                                category={datasheetsSet.name}
                                register={register}
                                customerInfo={{
                                  industryNameForPageLoadTracking,
                                  regionForPageLoadTracking,
                                  subIndustryNameForPageLoadTracking,
                                  targetAudienceForPageLoadTracking,
                                }}
                              />
                            ))}
                          </Rows>
                          {!!datasheetsSet.children.length && (
                            <Accordion variant="tableItem">
                              {datasheetsSet.children.map(
                                (
                                  childSet: IDatasheetTypeSetChildren,
                                  index
                                ) => (
                                  <AccordionItem
                                    dataTestId={`${dataTestIdPrefix}-Accordion-Content-${index}`}
                                    key={childSet.name}
                                    buttonClasses={classNames({
                                      'tw-border-t tw-border-grey-20':
                                        index === 0,
                                    })}
                                    isOpen={expandAll}
                                    variant="tableItem"
                                    title={childSet.name}
                                  >
                                    <Rows
                                      className="tw-pl-xs lg:tw-pl-sm"
                                      borderTop
                                      borderBottom={false}
                                    >
                                      {childSet.value.map((childDatasheet) => (
                                        <DownloadItemRow
                                          dataTestId={`${dataTestIdPrefix}-Datasheet-Item`}
                                          selectedDocuments={selectedDocuments}
                                          setSelectedDocuments={
                                            setSelectedDocuments
                                          }
                                          removeDocument={removeDocument}
                                          key={childDatasheet.id}
                                          value={childDatasheet}
                                          label={addLabel}
                                          name={`${childDatasheet.name} (${childDatasheet.location} - ${childDatasheet.language})`}
                                          addLabel={addLabel}
                                          fileUrl={`${downloadEndpoint}merged?selectedFiles=${childDatasheet.id}`}
                                          cultureCode={
                                            childDatasheet.cultureCode
                                          }
                                          fileName={childDatasheet.fileName}
                                          downloadLabel={downloadLabel}
                                          category={datasheetsSet.name}
                                          register={register}
                                          customerInfo={{
                                            industryNameForPageLoadTracking,
                                            regionForPageLoadTracking,
                                            subIndustryNameForPageLoadTracking,
                                            targetAudienceForPageLoadTracking,
                                          }}
                                        />
                                      ))}
                                    </Rows>
                                  </AccordionItem>
                                )
                              )}
                            </Accordion>
                          )}
                        </div>
                      ))}
                    </AccordionItem>
                  )
                )}
              </Accordion>
            </>
          )}
        </div>
      </form>
      {/*
      -----------------
      EXPANDABLE BANNER
      -----------------
      */}
      {selectedDocuments.length > 0 && (
        <ExpandableBanner
          text={replaceValuesInLabel(documentsAddedLabel, [
            `${selectedDocuments?.length ?? 0}`,
          ])}
          dataTestId={dataTestIdPrefix}
        >
          <div
            tabIndex={0}
            className="tw-flex tw-h-full tw-flex-col xl:tw-container xl:tw-grid xl:tw-grid-cols-12 xl:tw-gap-md xl:tw-pt-24"
          >
            <div className="tw-order-2 tw-col-span-full xl:tw-order-1 xl:tw-col-span-3">
              <div className="tw-flex tw-flex-col tw-gap-xs tw-bg-white tw-p-xs md:tw-p-md lg:tw-p-xl xl:tw-p-0">
                <Button
                  site={site}
                  as="link"
                  href={`${downloadEndpoint}merged?selectedFiles=${selectedDocuments.map(
                    (doc) => doc.id
                  )}`}
                  download
                  label={downloadAsSingleLabel}
                  dataTestId={`${dataTestIdPrefix}-Expandable-Banner-Download-Single-Button`}
                />
                <Button
                  as="link"
                  href={`${downloadEndpoint}compressed?selectedFiles=${selectedDocuments.map(
                    (doc) => doc.id
                  )}`}
                  download
                  modifier="outline"
                  variant="grey80"
                  label={downloadAsMultipleLabel}
                  dataTestId={`${dataTestIdPrefix}-Expandable-Banner-Download-Multiple-Button`}
                />
              </div>
            </div>
            <div className="tw-col-span-full tw-grow tw-overflow-y-auto tw-px-xs tw-pt-16 md:tw-px-md lg:tw-px-xl xl:tw-order-2 xl:tw-col-span-9 xl:tw-overflow-visible xl:tw-px-0 xl:tw-pb-24 xl:tw-pt-0">
              <Rows borderTop>
                <div className="tw-flex tw-justify-between tw-py-xs">
                  <Text variant="body-14-semibold">{documentsLabel}</Text>
                  <button
                    onClick={() => removeAllDocuments()}
                    data-testid={`${dataTestIdPrefix}-Expandable-Banner-Remove-All-Button`}
                  >
                    <Text
                      variant="body-14-semibold"
                      className="tw-underline-offset-2 hover:tw-underline"
                    >
                      {removeAllLabel}
                    </Text>
                  </button>
                </div>
                {selectedDocuments.map((document, index) => (
                  <div
                    key={document.id}
                    className="tw-flex tw-justify-between tw-py-xs tw-text-sm"
                  >
                    <Text variant="body-small" className="text-grey-80">
                      {`${document.name} (${document.location} - ${document.language})`}
                    </Text>
                    <button
                      type="button"
                      onClick={() => removeDocument(document.id)}
                      data-testid={`${dataTestIdPrefix}-Expandable-Banner-Document-Remove-Button-${index}`}
                    >
                      <Text
                        variant="body-small"
                        className="tw-underline-offset-2 hover:tw-underline"
                      >
                        {removeLabel}
                      </Text>
                    </button>
                  </div>
                ))}
              </Rows>
            </div>
          </div>
        </ExpandableBanner>
      )}
    </section>
  );
};

export default TechnicalDownloadSearchPage;
